import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { ForeignNationalsAPI } from "../../../api/foreignNationals";
import { IndividualsAPI } from "../../../api/individuals";
import { MattersAPI } from "../../../api/matters";
import type { MatterDTO } from "../../../api/types/matter";
import {
  MIXPANEL_EVENTS,
  track
} from "../../../services/analytics/mixpanelClient";
import { useAppTabsStore } from "../../appTabs/context/appTabsStore";
import { uploadFilesToCase } from "../../caseFiles/upload/useStagedUpload";
import { casePath, NEW_CASE_PATH } from "../../caseIde/routes";
import { CompaniesAPI } from "../../companies/api/companiesApi";
import { refreshCompanies } from "../../companies/queries";
import {
  buildCreateCasePayload,
  isNoticeWorkflow
} from "../../clientProfile/tabs/cases/useCreateCaseForm";
import type { NewCaseValues, SelectedClient } from "../types";
import {
  asksPetitioner,
  baseVisaOf,
  companyPetitionerFor,
  isEmailExistsError,
  petitionerUpdate,
  toCreateCaseValues,
  toCreateClientPayload,
  toSelectedClient
} from "../utils";

export const EMAIL_EXISTS_MESSAGE = "Looks like this client already exists.";

export type SubmitOutcome =
  | { status: "created"; matter: MatterDTO; clientId: string }
  | { status: "clientError"; message: string; emailExists: boolean }
  | {
      status: "caseError";
      message: string;
      /** The client this submit created before the case call failed. */
      createdClient?: SelectedClient;
    };

type SubmitOptions = {
  /**
   * The client was just created. The wizard keeps it so a retry after a
   * failed case call reuses it instead of creating the client again.
   */
  onClientCreated: (client: SelectedClient) => void;
};

const messageOf = (e: unknown, fallback: string): string =>
  e instanceof Error && e.message ? e.message : fallback;

/**
 * The notice, awaited so the case opens with it being read. A failed upload
 * is named in a toast.
 */
const uploadNotice = async (individualId: string, notice: File | null) => {
  if (!notice) return;
  const warn = (failed: string[]) => {
    if (!failed.length) return;
    toast.warning(
      `Case created, but ${failed.length} file${
        failed.length === 1 ? "" : "s"
      } didn't upload`,
      { description: `${failed.join(", ")}. Add them from Case Files.` }
    );
  };
  try {
    warn((await uploadFilesToCase(individualId, [notice])).failed);
  } catch {
    warn([notice.name]);
  }
};

/**
 * Create the client (for a new one), then the case under it, upload its
 * notice, record the petitioner, and open the case. Returns where it
 * stopped on failure.
 */
export const submitNewCase = async (
  values: NewCaseValues,
  currentUserId: string,
  { onClientCreated }: SubmitOptions
): Promise<SubmitOutcome> => {
  let createdClient =
    values.mode === "new" ? values.createdClient ?? undefined : undefined;
  let clientId =
    values.mode === "existing"
      ? values.client?.id ?? ""
      : createdClient?.id ?? "";

  if (values.mode === "new" && !clientId) {
    try {
      const created = await ForeignNationalsAPI.create(
        toCreateClientPayload(values, currentUserId)
      );
      clientId = created.uid;
      createdClient = toSelectedClient(created);
      onClientCreated(createdClient);
    } catch (e) {
      const message = messageOf(
        e,
        "Failed to create client. Please try again."
      );
      return isEmailExistsError(message)
        ? {
            status: "clientError",
            message: EMAIL_EXISTS_MESSAGE,
            emailExists: true
          }
        : { status: "clientError", message, emailExists: false };
    }
  }

  if (!clientId) {
    return {
      status: "clientError",
      message: "Choose a client for this case.",
      emailExists: false
    };
  }

  const payload = buildCreateCasePayload(
    toCreateCaseValues(values),
    currentUserId
  );
  if (!payload) {
    return {
      status: "caseError",
      message: "Choose a workflow for this case.",
      createdClient
    };
  }

  let matter: MatterDTO;
  try {
    matter = await MattersAPI.createForForeignNational(clientId, payload);
  } catch (e) {
    return {
      status: "caseError",
      message: messageOf(e, "We couldn't create the case. Please try again."),
      createdClient
    };
  }

  await uploadNotice(matter.individual_id, values.noticeFile);
  if (!asksPetitioner(values)) return { status: "created", matter, clientId };

  const company = companyPetitionerFor(baseVisaOf(values), values);
  if (company) {
    try {
      await CompaniesAPI.link(company.id, [matter.individual_id], {
        entity_id: company.entity_id,
        job_description_id: company.job_description_id,
        worksite_id: company.worksite_id
      });
      void refreshCompanies();
    } catch {
      toast.warning(`Case created, but it isn't linked to ${company.name}`, {
        description: "You can link it from the company's Employees tab."
      });
    }
    return { status: "created", matter, clientId };
  }

  const update = petitionerUpdate(
    baseVisaOf(values),
    values.petitioner,
    values.petitionerKind
  );
  if (update) {
    try {
      await IndividualsAPI.updateIndividual(matter.individual_id, update);
    } catch {
      toast.warning("Case created, but the petitioner wasn't saved", {
        description: "You can add it from the case settings."
      });
    }
  }

  return { status: "created", matter, clientId };
};

/** The wizard's tab would reopen the wizard; the new case's tab takes its place. */
const closeWizardTab = () => {
  const { tabs, close } = useAppTabsStore.getState();
  const wizard = tabs.find((t) => t.route.split("?")[0] === NEW_CASE_PATH);
  if (wizard) close(wizard.id);
};

export const useSubmitNewCase = (currentUserId: string) => {
  const navigate = useNavigate();

  return useCallback(
    async (
      values: NewCaseValues,
      options: SubmitOptions
    ): Promise<SubmitOutcome> => {
      const outcome = await submitNewCase(values, currentUserId, options);
      if (outcome.status === "created") {
        track(MIXPANEL_EVENTS.CASE_CREATED, {
          visa_type: baseVisaOf(values),
          is_rfe: isNoticeWorkflow(values.workflow),
          workflow: values.workflow,
          client_mode: values.mode
        });
        // Back from the case goes to where the wizard was opened from.
        navigate(
          casePath({
            individualId: outcome.matter.individual_id,
            foreignNationalId: outcome.clientId
          }),
          { replace: true }
        );
        closeWizardTab();
      }
      return outcome;
    },
    [currentUserId, navigate]
  );
};
